import { z, type ZodIssue } from "zod";

import i18n from "@/shared/lang/i18n";

/**
 * Localizes the Zod issues that reach the UI **without a message of their own**.
 *
 * Zod resolves a message in this order: the schema's own, then `params.error`, then this `customError`, then
 * `localeError`, then its built-in default. Being third, this file never sees an issue that a validator
 * already worded — and every business rule in the repo passes its own `t(...)` string (document length by
 * type, phone validity by country, the document-type-per-country catalogue…), so none of them are affected by
 * anything here. What does land here is the plain `z.string().min(1)` kind of bound, which until now answered
 * in hardcoded Spanish regardless of the dashboard's language.
 *
 * **Translation happens at validation time, not at registration.** `customError` runs once per issue, as the
 * form validates, so reading `i18n.t` inside it picks up whatever language is active right then. That is why
 * nothing has to be re-registered on `languageChanged`, and why the singleton is read directly instead of
 * through `useTranslations` — this is not a hook and never runs in a component. Same reasoning, and same
 * approach, as `payments/utils/validatePaymentMethodFields.ts`.
 *
 * **Known limitation:** a message already on screen does not re-translate when the language changes. React
 * Hook Form stores the resolved string in `formState`, so it updates on the next validation of that field
 * (blur under `mode: "onTouched"`, or the next submit). Fixing it would mean triggering revalidation from a
 * language effect — validation machinery, in 21 forms or in `useZodForm` for all 105 callers — for a case
 * that only arises when someone switches language with errors already showing. Not worth it.
 *
 * Two structural rules for anyone editing this file:
 *
 * - **Keep the branches.** Each `if` / `switch` arm below distinguishes a genuinely different sentence (a bound
 *   on a string, on an array, on a number). Collapsing them does not change what validates, but it does make
 *   a field say the wrong thing.
 * - **Keep `default: return undefined`.** That is what lets codes this map does not handle — `custom`,
 *   `unrecognized_keys`, `invalid_key`, `invalid_element` — fall through to Zod's own default. Completing the
 *   switch would silently reword every `.refine()` that carries no message.
 */

type LocalizedIssue = Pick<ZodIssue, "code" | "input"> & {
  minimum?: number | bigint;
  maximum?: number | bigint;
  format?: string;
  origin?: string;
  type?: string;
  expected?: string;
  received?: string;
  unionErrors?: unknown;
  pattern?: { source?: string };
};

/**
 * `minimum` / `maximum` are `number | bigint`, and i18next cannot interpolate a bigint — stringify at the
 * boundary rather than letting one reach the formatter.
 */
const tr = (key: string, vars?: Record<string, string>): string => i18n.t(`global:${key}`, vars) as string;

const isArray = (value: unknown): boolean => Array.isArray(value);

const translateTooSmall = (issue: LocalizedIssue): string => {
  const min = String(issue.minimum);
  const inputIsArray = isArray(issue.input);

  if (issue.origin === "string" || typeof issue.input === "string") {
    if (issue.minimum === 1) return tr("input_error_required");
    return tr("input_error_min_length", { length: min });
  }
  if (issue.origin === "array" || inputIsArray) {
    return tr("input_error_min_items", { items: min });
  }
  if (issue.origin === "number" || typeof issue.input === "number") {
    return tr("input_error_min_value", { min });
  }
  return tr("input_error_too_small", { min });
};

const translateTooBig = (issue: LocalizedIssue): string => {
  const max = String(issue.maximum);
  const inputIsArray = isArray(issue.input);

  if (issue.origin === "string" || typeof issue.input === "string") {
    return tr("input_error_max_length", { length: max });
  }
  if (issue.origin === "array" || inputIsArray) {
    return tr("input_error_max_items", { items: max });
  }
  if (issue.origin === "number" || typeof issue.input === "number") {
    return tr("input_error_max_value", { max });
  }
  return tr("input_error_too_big", { max });
};

const translateInvalidFormat = (issue: LocalizedIssue): string => {
  const format = issue.format ?? "";
  switch (format) {
    case "email":
      return tr("input_error_invalid_email");
    case "url":
      return tr("input_error_invalid_url");
    case "uuid":
      return tr("input_error_invalid_uuid");
    case "regex":
      return tr("input_error_invalid_format");
    default:
      return tr("input_error_invalid_format");
  }
};

const translateInvalidType = (issue: LocalizedIssue): string => {
  if (issue.input === undefined || issue.input === null) {
    return tr("input_error_required");
  }
  // Was `Se esperaba ${issue.expected}`, where `expected` is the raw Zod type name ("string", "number") —
  // an untranslatable word inside a translated sentence, and meaningless to whoever reads it. An <input>
  // always hands over a string and this repo types money as decimal strings, so the branch is unreachable
  // in practice anyway.
  return tr("input_error_invalid_value");
};

// The list of accepted values is deliberately not rendered. It is the raw enum wire values ("M", "F"), which
// leak an internal vocabulary into a sentence the user is meant to act on.
const translateInvalidValue = (issue: LocalizedIssue): string => {
  // An untouched `z.enum` select does NOT report `invalid_type` — Zod raises `invalid_value` with no `input`
  // at all. Without this branch, a field nobody has filled in says "the value is not valid", which describes
  // a value that does not exist and tells the operator nothing about what to do.
  if (issue.input === undefined || issue.input === null) {
    return tr("input_error_required");
  }
  return tr("input_error_invalid_value");
};

const customError = (issue: LocalizedIssue): string | undefined => {
  switch (issue.code) {
    case "invalid_type":
      return translateInvalidType(issue);
    case "too_small":
      return translateTooSmall(issue);
    case "too_big":
      return translateTooBig(issue);
    case "invalid_format":
      return translateInvalidFormat(issue);
    case "invalid_value":
      return translateInvalidValue(issue);
    case "not_multiple_of":
      return tr("input_error_invalid_value");
    case "invalid_union":
      return tr("input_error_invalid_value");
    default:
      return undefined;
  }
};

export function registerColocaZodErrorMap(): void {
  z.config({
    customError: (issue) => customError(issue as LocalizedIssue),
  });
}
